import { createClient } from '@supabase/supabase-js';
import { nanoid } from 'nanoid';
import { Location } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const storageBucket = import.meta.env.VITE_STORAGE_BUCKET;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:', {
    url: !!supabaseUrl,
    key: !!supabaseAnonKey
  });
}

// Log the configuration for debugging
console.log('Supabase configuration:', {
  url: supabaseUrl,
  hasKey: !!supabaseAnonKey,
  bucket: storageBucket
});

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    storageKey: 'supabase.auth.token',
    flowType: 'pkce'
  },
  global: {
    headers: {
      'X-Client-Info': 'moikutok-web'
    }
  }
});

// Debug logging for auth state
supabase.auth.onAuthStateChange((event, session) => {
  console.log('Supabase auth state change:', {
    event,
    hasSession: !!session,
    hasAccessToken: !!session?.access_token,
    userEmail: session?.user?.email,
    emailConfirmed: !!session?.user?.email_confirmed_at
  });
});

// Unified storage upload function using only Supabase SDK
export const uploadFileToStorage = async (
  bucketName: string, 
  fileName: string, 
  file: File,
  requireAuth: boolean = true
): Promise<string> => {
  try {
    console.log('Starting unified storage upload:', { 
      bucketName, 
      fileName, 
      fileSize: file.size,
      fileType: file.type,
      requireAuth 
    });
    
    // Check authentication if required
    if (requireAuth) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required for file upload');
      }
      console.log('User authenticated for upload');
    }
    
    // Use Supabase SDK for upload - this automatically handles:
    // - Correct endpoint (/storage/v1/object/...)
    // - JWT authorization header
    // - Proper multipart/form-data formatting
    const { data, error } = await supabase.storage
      .from(bucketName)
      .upload(fileName, file, {
        upsert: true,
        contentType: file.type
      });
    
    if (error) {
      console.error('Supabase storage upload error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }
    
    if (!data?.path) {
      throw new Error('Upload succeeded but no path returned');
    }
    
    console.log('Upload successful, path:', data.path);
    
    // Generate public URL using SDK
    const { data: urlData } = supabase.storage
      .from(bucketName)
      .getPublicUrl(data.path);
    
    if (!urlData?.publicUrl) {
      throw new Error('Failed to generate public URL');
    }
    
    console.log('Generated public URL:', urlData.publicUrl);
    
    // Validate the URL format
    if (!urlData.publicUrl.startsWith('http')) {
      throw new Error(`Invalid public URL format: ${urlData.publicUrl}`);
    }
    
    return urlData.publicUrl;
    
  } catch (error) {
    console.error('Storage upload failed:', error);
    throw error;
  }
};

// Test storage bucket access
export const testStorageAccess = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    console.log('Testing storage access with session:', !!session);
    console.log('Testing storage bucket:', storageBucket || 'location-images');
    
    // Test bucket listing
    const { data, error } = await supabase.storage
      .from(storageBucket || 'location-images')
      .list('', { limit: 1 });
    
    if (error) {
      console.error('Storage access test failed:', error);
      console.error('Storage URL being used:', `${supabaseUrl}/storage/v1`);
      return false;
    }
    
    console.log('Storage access test successful, found', data?.length || 0, 'items');
    return true;
  } catch (error) {
    console.error('Storage access test error:', error);
    return false;
  }
};

export const checkFavoriteStatus = async (locationId: string): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return false;

    const { data, error } = await supabase
      .from('favorites')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('location_id', locationId)
      .maybeSingle();

    if (error) throw error;
    return Boolean(data);
  } catch (error) {
    console.error('Error checking favorite status:', error);
    return false;
  }
};

export const toggleFavorite = async (locationId: string, isFavorite: boolean) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Not authenticated');
    }

    if (isFavorite) {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .match({ user_id: session.user.id, location_id: locationId });

      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('favorites')
        .insert({
          user_id: session.user.id,
          location_id: locationId
        });

      if (error && error.code !== '23505') throw error; // Ignore unique constraint violations
    }

    return { error: null };
  } catch (error) {
    console.error('Error toggling favorite:', error);
    return { error };
  }
};

export const getUserFavorites = async (): Promise<Location[]> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];

    // First get all favorite location IDs
    const { data: favorites, error: favoritesError } = await supabase
      .from('favorites')
      .select('location_id')
      .eq('user_id', session.user.id);

    if (favoritesError) throw favoritesError;
    if (!favorites?.length) return [];

    // Then fetch the actual locations
    const locationIds = favorites.map(f => f.location_id);
    const { data: locations, error: locationsError } = await supabase
      .from('locations')
      .select('*')
      .in('id', locationIds);

    if (locationsError) throw locationsError;
    if (!locations) return [];

    return locations.map(location => ({
      ...location,
      shortDescription: location.short_description,
      images: location.images.map((image: string) => 
        image.startsWith('http') ? image : `${supabaseUrl}/storage/v1/object/public/${storageBucket}/${image}`
      ),
      tags: location.tags || []
    }));
  } catch (error) {
    console.error('Error loading favorites:', error);
    return [];
  }
};

export const addLocation = async (
  location: Omit<Location, 'id'>
): Promise<{ data: Location | null; error: string | null }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    const short_id = nanoid(8);

    console.log('Adding location with images:', location.images);

    const { data, error } = await supabase
      .from('locations')
      .insert([
        {
          name: location.name,
          description: location.description,
          short_description: location.shortDescription,
          category: location.category,
          coordinates: location.coordinates,
          images: location.images, // These should now be full URLs
          tags: location.tags || [],
          status: 'pending',
          is_verified: false,
          short_id
        }
      ])
      .select()
      .single();

    if (error) throw error;

    return {
      data: {
        id: data.id,
        name: data.name,
        description: data.description,
        shortDescription: data.short_description,
        category: data.category,
        coordinates: data.coordinates,
        images: data.images,
        tags: data.tags || [],
        status: data.status,
        created_at: data.created_at,
        updated_at: data.updated_at,
        is_verified: data.is_verified,
        search_vector: data.search_vector,
        short_id: data.short_id
      },
      error: null
    };
  } catch (error) {
    console.error('Error adding location:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Failed to add location'
    };
  }
};

export const updateLocation = async (location: Location): Promise<{ error: string | null }> => {
  try {
    console.log('Updating location with images:', location.images);

    const { error } = await supabase
      .from('locations')
      .update({
        name: location.name,
        description: location.description,
        short_description: location.shortDescription,
        category: location.category,
        coordinates: location.coordinates,
        images: location.images, // These should now be full URLs
        tags: location.tags || [],
        status: location.status,
        updated_at: new Date().toISOString()
      })
      .eq('id', location.id);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error updating location:', error);
    return { error: error instanceof Error ? error.message : 'Failed to update location' };
  }
};

export const deleteLocation = async (id: string): Promise<{ error: string | null }> => {
  try {
    const { error } = await supabase
      .from('locations')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting location:', error);
    return { error: error instanceof Error ? error.message : 'Failed to delete location' };
  }
};

export const uploadAvatar = async (file: File, userId: string): Promise<string> => {
  try {
    // Validate file
    if (!file || file.size > 5 * 1024 * 1024) {
      throw new Error('Файл занадта вялікі. Максімальны памер - 5MB.');
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Недапушчальны тып файла. Дазволены толькі JPEG, PNG, GIF і WebP.');
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `avatar.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    // Upload file to storage using unified function
    return await uploadFileToStorage('avatars', filePath, file, true);
  } catch (error) {
    console.error('Error uploading avatar:', error);
    throw error;
  }
};

export const createUserProfile = async (
  profile: { name?: string; avatar_url?: string }
): Promise<{
  data: { name: string | null; avatar_url: string | null } | null;
  error: string | null;
}> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      return { data: null, error: 'Not authenticated' };
    }

    // Use the RPC function to create or update profile
    const { data, error } = await supabase.rpc('create_or_update_profile', {
      user_id: session.user.id,
      profile_name: profile.name || null,
      profile_avatar_url: profile.avatar_url || null
    });

    if (error) {
      console.error('Error creating profile:', error);
      throw error;
    }

    return {
      data: { name: data.name ?? null, avatar_url: data.avatar_url ?? null },
      error: null
    };
  } catch (error) {
    console.error('Error creating profile:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Не атрымалася стварыць профіль'
    };
  }
};

export const updateUserProfile = async (
  updates: { name?: string; avatar_url?: string }
): Promise<{
  data: { name: string | null; avatar_url: string | null } | null;
  error: string | null;
}> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      return { data: null, error: 'Not authenticated' };
    }

    // Use the RPC function to create or update profile
    const { data, error } = await supabase.rpc('create_or_update_profile', {
      user_id: session.user.id,
      profile_name: updates.name || null,
      profile_avatar_url: updates.avatar_url || null
    });

    if (error) {
      console.error('Error updating profile:', error);
      throw error;
    }

    return {
      data: { name: data.name ?? null, avatar_url: data.avatar_url ?? null },
      error: null
    };
  } catch (error) {
    console.error('Error updating profile:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Не атрымалася абнавіць профіль'
    };
  }
};

export const deleteUserAccount = async (): Promise<{ error: string | null }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      return { error: 'Not authenticated' };
    }

    console.log('Calling Edge Function to delete user account...');

    // Call the Edge Function to delete the user account
    const { data, error } = await supabase.functions.invoke('delete-user', {
      body: { userId: session.user.id },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Edge Function error:', error);
      throw new Error(`Edge Function error: ${error.message}`);
    }

    if (!data?.success) {
      console.error('Edge Function returned error:', data);
      throw new Error(data?.error || 'Edge Function returned an error');
    }

    console.log('User account deleted successfully via Edge Function');

    // Clear local storage after successful deletion
    try {
      localStorage.clear();
      sessionStorage.clear();
      console.log('Local storage cleared');
    } catch (storageError) {
      console.warn('Error clearing local storage:', storageError);
    }

    return { error: null };

  } catch (error) {
    console.error('Error deleting user account:', error);
    return {
      error: error instanceof Error ? error.message : 'Не атрымалася выдаліць акаўнт'
    };
  }
};

export const markVisited = async (locationId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error("Not authenticated");
    
    const { error } = await supabase
      .from("visits")
      .insert({ user_id: session.user.id, location_id: locationId });
    
    if (error && error.code !== "23505") throw error;
    return { error: null };
  } catch (error) {
    console.error("Error marking visit:", error);
    return { error };
  }
};

export const updateVisitReview = async (locationId: string, liked: boolean) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error("Not authenticated - no user session");
    }

    // Use Supabase SDK for the update
    const { data, error } = await supabase
      .from("visits")
      .update({ liked })
      .eq("user_id", session.user.id)
      .eq("location_id", locationId)
      .select(); // Return updated data for verification

    if (error) {
      console.error('Supabase update error:', error);
      return { error };
    }

    if (!data || data.length === 0) {
      // Update went through but no rows returned - this can happen with RLS
      console.warn('Visit review update returned no rows');
      return { error: null };
    }

    const updatedRow = data[0];
    if (updatedRow.liked !== liked) {
      console.warn(`Update verification mismatch: expected ${liked}, got ${updatedRow.liked}`);
    }

    return { error: null };
  } catch (error) {
    console.error("Error updating review:", error);
    return { error };
  }
};

export const getVisitStatus = async (locationId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    
    const { data, error } = await supabase
      .from("visits")
      .select("liked")
      .eq("user_id", session.user.id)
      .eq("location_id", locationId)
      .maybeSingle();
    
    if (error || !data) return null;
    return data as { liked: boolean | null };
  } catch (error) {
    console.error("Error loading visit status:", error);
    return null;
  }
};

export const getVisitStats = async (locationId: string) => {
  try {
    const { data, count, error } = await supabase
      .from("visits")
      .select("liked", { count: "exact" })
      .eq("location_id", locationId);
    
    if (error) throw error;
    
    const likes = data?.filter(v => v.liked === true).length || 0;
    const dislikes = data?.filter(v => v.liked === false).length || 0;
    const total = count || 0;
    
    return { total, likes, dislikes };
  } catch (error) {
    console.error("Error getting stats:", error);
    return { total: 0, likes: 0, dislikes: 0 };
  }
};

export const getUserVisitedLocations = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];
    
    const { data, error } = await supabase
      .from("visits")
      .select(`liked, location_id, locations:location_id(*)`)
      .eq("user_id", session.user.id);
    
    if (error) throw error;
    if (!data) return [];
    
    return data.map(v => ({
      liked: v.liked as boolean | null,
      location: {
        ...v.locations,
        shortDescription: v.locations.short_description,
        images: v.locations.images.map((img: string) => 
          img.startsWith("http") ? img : `${supabaseUrl}/storage/v1/object/public/${storageBucket}/${img}`
        ),
        tags: v.locations.tags || []
      }
    }));
  } catch (error) {
    console.error("Error loading visited locations:", error);
    return [];
  }
};